import type {
  AuthGoogleSignInOutcome,
  AuthGoogleSignInRequest,
  AuthProviders,
  OperatorLocale,
} from "@backchannel/tax-contracts";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type AppEnvConfig, describeAuthMisconfiguration } from "~/config/env";
import { logDiagnostic } from "~/lib/redact";
import { AuthError } from "./errors";
import { type PlatformAuthClient, createPlatformAuthClient } from "./platform";
import { refreshSharedSession } from "./shared-refresh";
import { createSingleFlight } from "./single-flight";
import { type TokenSet, type TokenStore, tokenStore as defaultTokenStore } from "./token-store";

export type AuthStatus =
  | "initialising"
  | "reconnecting"
  | "anonymous"
  | "authenticated"
  | "unconfigured"
  | "error";

export type AuthValue = {
  status: AuthStatus;
  reason: string | null;
  mode: "platform" | "mock";
  signIn: (returnTo?: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  requestPasswordReset: (email: string, locale?: OperatorLocale) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  acceptInvite: (token: string, password: string, displayName?: string) => Promise<void>;
  previewInvite: PlatformAuthClient["previewInvite"];
  signInWithPasskey: (email?: string) => Promise<void>;
  /**
   * WebAuthn conditional mediation (passkey autofill). Rejects (including on
   * abort) rather than mutating auth status on failure — this is a passive,
   * background offer, so the caller decides whether/how to react.
   */
  signInWithPasskeyAutofill: (signal: AbortSignal) => Promise<void>;
  /** Optional providers (Google) the deployed API has enabled. */
  listSignInProviders: () => Promise<AuthProviders>;
  /**
   * Finish the Google redirect: exchange the code for a platform session.
   * Resolves with how the operator was resolved (PROVISIONED = new reader).
   */
  completeGoogleSignIn: (request: AuthGoogleSignInRequest) => Promise<AuthGoogleSignInOutcome>;
  signOut: () => void;
  retrySession?: () => void;
  getIdToken: () => Promise<string | null>;
  tokenDiagnostics: () => { present: boolean; expiresInSeconds: number | null };
};

const AuthContext = createContext<AuthValue | null>(null);

const tabRefreshFlight = createSingleFlight<TokenSet>();

export function AuthProvider({
  config,
  children,
  tokenStore = defaultTokenStore,
  client,
}: {
  config: AppEnvConfig;
  children: ReactNode;
  tokenStore?: TokenStore;
  client?: PlatformAuthClient;
}) {
  const misconfiguration = describeAuthMisconfiguration(config);
  const [status, setStatus] = useState<AuthStatus>(() => {
    if (config.authMode === "mock") return "authenticated";
    if (misconfiguration) return "unconfigured";
    if (tokenStore.getIdToken()) return "authenticated";
    if (!tokenStore.getRefreshToken()) return "anonymous";
    return "initialising";
  });
  const [reason, setReason] = useState<string | null>(misconfiguration);
  const passkeyInFlight = useRef(createSingleFlight<TokenSet>());
  const authGeneration = useRef(0);

  const platform = useMemo(
    () => client ?? (config.authMode === "platform" ? createPlatformAuthClient(config) : null),
    [client, config],
  );

  const refreshTokens = useCallback(
    (
      refreshToken: string,
      source: "platform" | "passkey" = tokenStore.getSessionSource(),
    ): Promise<TokenSet> => {
      if (!platform) return Promise.reject(new Error("Identity client unavailable"));
      return tabRefreshFlight.run(`${source}:${refreshToken}`, () =>
        refreshSharedSession(tokenStore, refreshToken, (current, owner) =>
          owner === "passkey" ? platform.refreshPasskey(current, "") : platform.refresh(current),
        ),
      );
    },
    [platform, tokenStore],
  );

  useEffect(() => {
    if (config.authMode !== "platform" || !platform || misconfiguration) return;
    if (status !== "initialising") return;

    if (tokenStore.getIdToken()) {
      setStatus("authenticated");
      return;
    }

    const refreshToken = tokenStore.getRefreshToken();
    if (!refreshToken) {
      setStatus("anonymous");
      return;
    }

    const sessionSource = tokenStore.getSessionSource();
    const generation = authGeneration.current;
    let cancelled = false;
    refreshTokens(refreshToken, sessionSource)
      .then(() => {
        if (generation !== authGeneration.current) return;
        if (cancelled) return;
        setStatus("authenticated");
      })
      .catch((error: unknown) => {
        if (cancelled || generation !== authGeneration.current) return;
        logDiagnostic("auth", { step: "session-refresh", error });
        if (!tokenStore.getRefreshToken()) {
          setStatus("anonymous");
          return;
        }
        const stored = tokenStore.getStoredIdToken();
        const expiresAt = tokenStore.expiresAt();
        if (stored && expiresAt && expiresAt > Date.now()) {
          setStatus("authenticated");
          return;
        }
        if (
          error instanceof AuthError &&
          error.code === "401" &&
          tokenStore.getRefreshToken() === refreshToken
        ) {
          tokenStore.clear();
          setStatus("anonymous");
        } else {
          setStatus("reconnecting");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [config.authMode, platform, misconfiguration, refreshTokens, status, tokenStore]);

  useEffect(
    () =>
      tokenStore.subscribe((hasToken, identityChanged) => {
        if (identityChanged) {
          window.location.reload();
          return;
        }
        setStatus((current) => {
          if (current === "initialising") {
            return hasToken ? "authenticated" : current;
          }
          return hasToken ? "authenticated" : "anonymous";
        });
      }),
    [tokenStore],
  );

  const signIn = useCallback(
    async (_returnTo?: string) => {
      if (config.authMode === "mock") {
        setStatus("authenticated");
        return;
      }
      setStatus("anonymous");
    },
    [config.authMode],
  );

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      if (config.authMode === "mock") {
        setStatus("authenticated");
        return;
      }
      if (!platform) {
        setStatus("unconfigured");
        setReason(misconfiguration ?? "Identity client unavailable");
        return;
      }
      const generation = authGeneration.current;
      const tokens = await platform.passwordSignIn(email, password);
      if (generation !== authGeneration.current) return;
      tokenStore.set(tokens);
      setStatus("authenticated");
      setReason(null);
    },
    [platform, config.authMode, misconfiguration, tokenStore],
  );

  const signInWithPasskey = useCallback(
    async (email = "") => {
      if (config.authMode === "mock") {
        setStatus("authenticated");
        return;
      }
      if (!platform) {
        setStatus("unconfigured");
        setReason(misconfiguration ?? "Identity client unavailable");
        return;
      }
      const generation = authGeneration.current;
      const key = email.trim().toLowerCase() || "discoverable";
      const tokens = await passkeyInFlight.current.run(key, () => platform.passkeySignIn(email));
      if (generation !== authGeneration.current) return;
      tokenStore.set(tokens);
      setStatus("authenticated");
      setReason(null);
    },
    [platform, config.authMode, misconfiguration, tokenStore],
  );

  const signInWithPasskeyAutofill = useCallback(
    async (signal: AbortSignal) => {
      if (config.authMode === "mock" || !platform) return;
      const generation = authGeneration.current;
      const tokens = await platform.passkeyAutofill(signal);
      if (generation !== authGeneration.current) return;
      tokenStore.set(tokens);
      setStatus("authenticated");
      setReason(null);
    },
    [platform, config.authMode, tokenStore],
  );

  const requestPasswordReset = useCallback(
    async (email: string, locale?: OperatorLocale) => {
      if (config.authMode === "mock") return;
      if (!platform) {
        setStatus("unconfigured");
        setReason(misconfiguration ?? "Identity client unavailable");
        return;
      }
      await platform.requestPasswordReset(email, locale);
    },
    [platform, config.authMode, misconfiguration],
  );

  const resetPassword = useCallback(
    async (token: string, newPassword: string) => {
      if (config.authMode === "mock") return;
      if (!platform) {
        setStatus("unconfigured");
        setReason(misconfiguration ?? "Identity client unavailable");
        return;
      }
      await platform.resetPassword(token, newPassword);
    },
    [platform, config.authMode, misconfiguration],
  );

  const acceptInvite = useCallback(
    async (token: string, password: string, displayName?: string) => {
      if (config.authMode === "mock") {
        setStatus("authenticated");
        return;
      }
      if (!platform) {
        setStatus("unconfigured");
        setReason(misconfiguration ?? "Identity client unavailable");
        return;
      }
      const generation = authGeneration.current;
      const tokens = await platform.acceptInvite(token, password, displayName);
      if (generation !== authGeneration.current) return;
      tokenStore.set(tokens);
      setStatus("authenticated");
      setReason(null);
    },
    [platform, config.authMode, misconfiguration, tokenStore],
  );

  const previewInvite = useCallback(
    (token: string) => {
      if (!platform) {
        return Promise.reject(new Error("Identity client unavailable"));
      }
      return platform.previewInvite(token);
    },
    [platform],
  );

  const listSignInProviders = useCallback(async (): Promise<AuthProviders> => {
    if (config.authMode === "mock" || !platform) return { google: null };
    return platform.providers();
  }, [platform, config.authMode]);

  const completeGoogleSignIn = useCallback(
    async (request: AuthGoogleSignInRequest): Promise<AuthGoogleSignInOutcome> => {
      if (config.authMode === "mock") {
        setStatus("authenticated");
        return "EXISTING";
      }
      if (!platform) {
        setStatus("unconfigured");
        setReason(misconfiguration ?? "Identity client unavailable");
        return "EXISTING";
      }
      const generation = authGeneration.current;
      const { tokens, outcome } = await platform.googleSignIn(request);
      if (generation !== authGeneration.current) return outcome;
      tokenStore.set(tokens);
      setStatus("authenticated");
      setReason(null);
      return outcome;
    },
    [platform, config.authMode, misconfiguration, tokenStore],
  );

  const signOut = useCallback(() => {
    authGeneration.current += 1;
    tabRefreshFlight.clear();
    passkeyInFlight.current.clear();
    tokenStore.clear();
    setStatus("anonymous");
  }, [tokenStore]);

  const getIdToken = useCallback(async () => {
    if (config.authMode === "mock") return "mock-session";

    const current = tokenStore.getIdToken();
    if (current) return current;

    const refreshToken = tokenStore.getRefreshToken();
    if (refreshToken && platform) {
      const generation = authGeneration.current;
      try {
        const refreshed = await refreshTokens(refreshToken, tokenStore.getSessionSource());
        if (generation !== authGeneration.current) return null;
        return refreshed.idToken;
      } catch (error) {
        logDiagnostic("auth", { step: "refresh", error });
        const stored = tokenStore.getStoredIdToken();
        const expiresAt = tokenStore.expiresAt();
        if (stored && expiresAt && expiresAt > Date.now()) return stored;
      }
    }
    return null;
  }, [platform, config.authMode, refreshTokens, tokenStore]);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      reason,
      mode: config.authMode,
      signIn,
      signInWithPassword,
      requestPasswordReset,
      resetPassword,
      acceptInvite,
      previewInvite,
      signInWithPasskey,
      signInWithPasskeyAutofill,
      listSignInProviders,
      completeGoogleSignIn,
      signOut,
      getIdToken,
      retrySession: () => setStatus("initialising"),
      tokenDiagnostics: () => tokenStore.describe(),
    }),
    [
      acceptInvite,
      completeGoogleSignIn,
      config.authMode,
      getIdToken,
      listSignInProviders,
      previewInvite,
      reason,
      requestPasswordReset,
      resetPassword,
      signIn,
      signInWithPassword,
      signInWithPasskey,
      signInWithPasskeyAutofill,
      signOut,
      status,
      tokenStore,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
  return value;
}
